import React from 'react';
import { Row, Col, Typography, Flex } from 'antd';
import {
  PhoneOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { websiteKindLabel, WebsiteKindIcon } from './orgContactWeb';

const { Title, Text } = Typography;

const websiteHref = (url) => {
  if (!url || !String(url).trim()) return '';
  const u = String(url).trim();
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
};

/** Normalize display phone to a tel: href (digits and leading + only). */
const telHref = (phone) => {
  if (!phone || !String(phone).trim()) return '';
  const normalized = String(phone).trim().replace(/[^\d+]/g, '');
  return normalized ? `tel:${normalized}` : '';
};

/**
 * Dashboard org header: left = company text (Facebook-style info column),
 * right = cover + overlapping logo (main app / share-style).
 */
const OrgProfileCard = ({
  organizationName,
  organizationLogo,
  organizationCover,
  organizationTagline,
  organizationDescription,
  organizationPublicIdentifiers = [],
  organizationPhone,
  organizationEmail,
  organizationWebsites = [],
  showCover,
  showLogo,
  firstName,
}) => {
  const phoneTel = telHref(organizationPhone);

  return (
    <Row className="customer-org-profile-split" gutter={[24, 24]}>
      <Col xs={24} md={8} lg={8} xl={8}>
        <Flex vertical className="customer-org-info-column" gap={12}>
          <Flex vertical gap={4}>
            <Title level={3} className="customer-org-title" style={{ margin: 0 }}>
              {organizationName || 'Organization'}
            </Title>
            <Text type="secondary" className="customer-org-welcome">
              Welcome back, {firstName}
            </Text>
          </Flex>

          {organizationTagline ? (
            <Text strong style={{ fontSize: 15, color: 'var(--varexe-gray-800)' }}>
              {organizationTagline}
            </Text>
          ) : null}

          {organizationDescription ? (
            <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.6, display: 'block' }}>
              {organizationDescription.length > 280
                ? `${organizationDescription.slice(0, 280)}…`
                : organizationDescription}
            </Text>
          ) : null}

          {organizationPublicIdentifiers.length > 0 ? (
            <Flex vertical gap={4} style={{ marginTop: 4 }}>
              {organizationPublicIdentifiers.map((row, index) => (
                <Text type="secondary" style={{ fontSize: 13, display: 'block' }} key={index}>
                  {String(row.label).trim()}: {String(row.value).trim()}
                </Text>
              ))}
            </Flex>
          ) : null}

          <Flex vertical gap={10} className="customer-org-contact-list">
            {organizationPhone ? (
              <Flex align="flex-start" gap={10}>
                <PhoneOutlined className="customer-org-contact-icon" aria-hidden />
                <Flex vertical gap={2} style={{ minWidth: 0 }}>
                  <Text className="customer-org-contact-kind">Phone</Text>
                  {phoneTel ? (
                    <a
                      href={phoneTel}
                      style={{ fontSize: 14, color: 'var(--varexe-primary)' }}
                      aria-label={`Call us at ${organizationPhone}`}
                    >
                      {organizationPhone}
                    </a>
                  ) : (
                    <Text style={{ fontSize: 14 }}>{organizationPhone}</Text>
                  )}
                </Flex>
              </Flex>
            ) : null}
            {organizationEmail ? (
              <Flex align="flex-start" gap={10}>
                <MailOutlined className="customer-org-contact-icon" aria-hidden />
                <Flex vertical gap={2} style={{ minWidth: 0 }}>
                  <Text className="customer-org-contact-kind">Email</Text>
                  <a href={`mailto:${organizationEmail}`} style={{ fontSize: 14, color: 'var(--varexe-primary)' }}>
                    {organizationEmail}
                  </a>
                </Flex>
              </Flex>
            ) : null}
            {organizationWebsites.map((site, index) => {
              const href = websiteHref(site.url);
              if (!href) return null;
              const kind = websiteKindLabel(site);
              const linkText = site.name || site.url || 'Website';
              return (
                <Flex key={`org-site-${index}`} align="flex-start" gap={10}>
                  <WebsiteKindIcon site={site} />
                  <Flex vertical gap={2} style={{ minWidth: 0 }}>
                    <Text className="customer-org-contact-kind">{kind}</Text>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 14, color: 'var(--varexe-primary)', wordBreak: 'break-word' }}
                    >
                      {linkText}
                    </a>
                  </Flex>
                </Flex>
              );
            })}
          </Flex>

          {!organizationPhone && !organizationEmail && organizationWebsites.length === 0 && !organizationDescription && !organizationTagline && organizationPublicIdentifiers.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 13 }}>
              Contact details appear here when your organization adds them in Varexe.
            </Text>
          ) : null}
        </Flex>
      </Col>

      <Col xs={24} md={16} lg={16} xl={16}>
        <Flex
          vertical
          className={`customer-org-media-column ${showLogo ? 'has-logo-overlap' : ''}`}
        >
          <Flex className="customer-org-cover-box" vertical>
            {showCover ? (
              <Flex className="customer-org-cover has-cover">
                <img
                  src={organizationCover}
                  alt={organizationName}
                  className="customer-org-cover-image"
                />
              </Flex>
            ) : (
              <Flex className="customer-org-cover no-cover" />
            )}
            {showLogo && (
              <Flex className="customer-org-logo-frame" align="center" justify="center">
                <img
                  src={organizationLogo}
                  alt={organizationName}
                  className="customer-org-logo-image"
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      </Col>
    </Row>
  );
};

export default OrgProfileCard;
